/* TYGERO theme behaviour: menu drawer, hero anthem, product form, bag, verify. */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Menu drawer ---------- */
  function initMenu() {
    const menu = $('[data-menu]');
    const opener = $('[data-menu-open]');
    if (!menu || !opener) return;
    const close = () => {
      menu.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ty-locked');
      opener.focus();
    };
    opener.addEventListener('click', () => {
      menu.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ty-locked');
      $('[data-menu-close]', menu).focus();
    });
    $$('[data-menu-close]', menu).forEach(el => el.addEventListener('click', close));
    menu.addEventListener('keydown', e => {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab') return;
      const items = $$('a[href], button', menu.querySelector('.ty-menu__panel'));
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // In-page links (e.g. #capsules) close the drawer before scrolling.
    $$('a[href*="#"]', menu).forEach(a => a.addEventListener('click', () => { menu.hidden = true; document.body.classList.remove('ty-locked'); }));
  }

  /* ---------- Hero video + anthem ---------- */
  function initHero() {
    const video = $('[data-hero-video]');
    if (!video) return;
    const play = () => video.play().catch(() => {});
    play();
    const btn = $('[data-anthem]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const on = btn.getAttribute('aria-pressed') !== 'true';
      video.muted = !on;
      btn.setAttribute('aria-pressed', String(on));
      if (video.paused) {
        video.play().catch(() => {
          // Browser refused playback with sound: fall back to muted.
          video.muted = true;
          btn.setAttribute('aria-pressed', 'false');
          play();
        });
      }
    });
  }

  /* ---------- Money ---------- */
  function formatTotal(cents, currency) {
    try {
      return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : undefined, {
        style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0, minimumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /* ---------- Quantity steppers ---------- */
  function bindSteppers(root, onChange) {
    $$('[data-qty-step]', root).forEach(btn => {
      btn.addEventListener('click', () => {
        const input = $('[data-qty]', btn.parentElement);
        const min = Number(input.min || 0);
        const max = input.max ? Number(input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qtyStep)));
        if (next === Number(input.value)) return;
        input.value = next;
        onChange(input);
      });
    });
    $$('[data-qty]', root).forEach(input => input.addEventListener('change', () => onChange(input)));
  }

  /* ---------- Product ---------- */
  function initProduct() {
    const root = $('[data-product]');
    if (!root) return;
    const data = JSON.parse($('[data-product-json]', root).textContent);
    const form = $('[data-product-form]', root);
    const idInput = $('[data-variant-id]', root);
    const qty = $('[data-qty]', root);
    const addBtn = $('[data-add-button]', root);
    const priceEl = $('[data-price]', root);
    const finishEl = $('[data-finish-name]', root);
    const stockChip = $('[data-stock-chip]', root);
    const inStockLabel = stockChip ? stockChip.textContent : '';
    const mainImg = $('.ty-pdp__main img', root);
    const thumbs = $$('[data-thumb]', root);
    let variant = data.variants.find(v => String(v.id) === idInput.value) || data.variants[0];

    const showMedia = thumb => {
      if (!thumb || !mainImg) return;
      mainImg.src = thumb.dataset.src;
      mainImg.srcset = thumb.dataset.srcset;
      mainImg.alt = thumb.dataset.alt;
      thumbs.forEach(t => t.setAttribute('aria-current', String(t === thumb)));
    };
    thumbs.forEach(t => t.addEventListener('click', () => showMedia(t)));

    const clampQty = () => {
      const n = Math.min(data.maxQuantity, Math.max(1, parseInt(qty.value, 10) || 1));
      qty.value = n;
      return n;
    };
    const render = () => {
      const n = clampQty();
      idInput.value = variant.id;
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available
        ? `${addBtn.dataset.label} — ${formatTotal(variant.price * n, data.currency)}`
        : 'Sold out';
      if (priceEl) priceEl.textContent = variant.priceFormatted;
      if (finishEl && data.finishIndex !== null) finishEl.textContent = variant.options[data.finishIndex];
      if (stockChip) stockChip.textContent = variant.available ? inStockLabel : 'Sold out';
    };

    const optionButtons = $$('[data-option-index]', root);
    optionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.optionIndex);
        $$(`[data-option-index="${idx}"]`, root).forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
        const selected = variant.options.slice();
        selected[idx] = btn.dataset.value;
        variant = data.variants.find(v => v.options.every((o, i) => o === selected[i]))
          || data.variants.find(v => v.options[idx] === btn.dataset.value)
          || variant;
        // Keep every option row in sync with the resolved variant.
        variant.options.forEach((val, i) => $$(`[data-option-index="${i}"]`, root)
          .forEach(b => b.setAttribute('aria-pressed', String(b.dataset.value === val))));
        if (variant.mediaId) showMedia(thumbs.find(t => t.dataset.mediaId === String(variant.mediaId)));
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(null, '', url);
        render();
      });
    });

    bindSteppers(root, render);
    form.addEventListener('submit', clampQty);
    render();
  }

  /* ---------- Bag ---------- */
  function updateCount(count) {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.toggleAttribute('data-filled', count > 0);
    });
    $$('[data-cart-link]').forEach(el => el.setAttribute('aria-label', `Bag, ${count} items`));
  }

  function initCart(root = $('[data-cart]')) {
    if (!root) return;
    const sectionId = root.dataset.sectionId;

    const change = async (line, quantity) => {
      root.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${window.tygero.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [sectionId] })
        });
        if (!res.ok) throw new Error(String(res.status));
        const cart = await res.json();
        updateCount(cart.item_count);
        const html = cart.sections && cart.sections[sectionId];
        if (html) {
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart]');
          root.replaceWith(fresh);
          initCart(fresh);
          return;
        }
        location.reload();
      } catch (e) {
        location.reload();
      } finally {
        root.removeAttribute('aria-busy');
      }
    };

    bindSteppers(root, input => change(Number(input.dataset.line), Math.max(0, parseInt(input.value, 10) || 0)));
    $$('[data-remove]', root).forEach(a => a.addEventListener('click', e => {
      e.preventDefault();
      change(Number(a.dataset.line), 0);
    }));

    const ack = $('[data-ack]', root);
    const checkout = $('[data-checkout]', root);
    if (ack && checkout) {
      const sync = () => { checkout.disabled = !ack.checked; };
      ack.addEventListener('change', sync);
      sync();
    }
  }

  /* ---------- Verify ---------- */
  function initVerify() {
    const root = $('[data-verify]');
    if (!root) return;
    const codes = (root.dataset.codes || '').split(',').filter(Boolean);
    const input = $('[data-verify-input]', root);
    const hint = $('[data-verify-hint]', root);
    const cert = $('[data-cert]', root);
    const setState = state => {
      input.dataset.state = state;
      hint.dataset.state = state;
      hint.textContent = state === 'fail' ? hint.dataset.fail : hint.dataset.idle;
    };
    input.addEventListener('input', () => { setState('idle'); cert.hidden = true; });
    $('[data-verify-form]', root).addEventListener('submit', e => {
      e.preventDefault();
      const clean = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      const ok = codes.length ? codes.includes(clean) : clean.length >= 10;
      setState(ok ? 'ok' : 'fail');
      cert.hidden = !ok;
      if (ok) {
        $('[data-cert-batch]', cert).textContent = input.value.trim().toUpperCase();
        cert.focus();
      }
    });
  }

  initMenu();
  initHero();
  initProduct();
  initCart();
  initVerify();
})();
